import React, { useState } from 'react';
import { FileTextIcon, PrinterIcon, DownloadIcon, EyeIcon, CheckCircleIcon, ClipboardCheckIcon, BuildingIcon, TruckIcon, PackageIcon, CalendarIcon, CircleDollarSignIcon, MapPinIcon } from 'lucide-react';
import { QuotePreview } from './QuotePreview';
export function QuoteReviewStep({
  quoteData,
  quoteType
}) {
  const [showPreview, setShowPreview] = useState(false);
  // Generate a random quote number
  const quoteNumber = `Q${Math.floor(Math.random() * 100000).toString().padStart(5, '0')}`;
  // Calculate totals
  const calculateSubtotal = () => {
    if (quoteType === 'Pivot') {
      return quoteData.pivots.reduce((sum, pivot) => sum + (pivot.price || 0), 0);
    } else if (quoteType === 'Spare Parts') {
      return quoteData.parts.reduce((sum, part) => sum + (part.quantity * part.unitPrice || 0), 0);
    } else {
      return quoteData.items.reduce((sum, item) => sum + (item.price || 0), 0);
    }
  };
  const subtotal = calculateSubtotal();
  const discount = quoteData.quoteDetails?.discount || 0;
  const discountAmount = subtotal * (discount / 100);
  const shippingTotal = quoteData.shipping?.shipments?.reduce((sum, shipment) => sum + (shipment.totalPrice || 0), 0) || 0;
  const total = subtotal - discountAmount + shippingTotal;
  return <div className="space-y-8" data-id="element-5014" data-mp="1ispq71ovfmly">
      <div className="text-white text-xl font-medium mb-6" data-id="element-5015" data-mp="1itr4df16op7w">Quote Review</div>
      {/* Summary Card */}
      <div className="bg-slate-800/50 rounded-xl p-5 border border-blue-800/30" data-id="element-5016" data-mp="1nqr8vlidxjww">
        <div className="flex items-center mb-4" data-id="element-5017" data-mp="1sq3y2cwqb32n">
          <ClipboardCheckIcon size={20} className="text-green-400 mr-2" data-id="element-5018" data-mp="1sosv2fb3xzcl" />
          <h3 className="text-white font-medium" data-id="element-5019" data-mp="1xix18wcju3cm">Quote Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-5020" data-mp="22gyntr96oksu">
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5021" data-mp="26iab3q4l4txv">
            <div className="text-sm text-blue-300 mb-1" data-id="element-5022" data-mp="26k3mfiq582c7">Quote Number</div>
            <div className="text-white font-medium" data-id="element-5023" data-mp="2bieubnwvo2rs">{quoteNumber}</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5024" data-mp="becdokzbxg0u">
            <div className="text-sm text-blue-300 mb-1" data-id="element-5025" data-mp="bcrhkqxd9p0v">Quote Type</div>
            <div className="text-white font-medium" data-id="element-5026" data-mp="7f3kpic45m6c">{quoteType} Quote</div>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5027" data-mp="l5p4radb025t">
            <div className="text-sm text-blue-300 mb-1" data-id="element-5028" data-mp="gegs85gog0of">Issue Date</div>
            <div className="text-white font-medium" data-id="element-5029" data-mp="gcdut0d7359o">
              {new Date().toLocaleDateString()}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-5030" data-mp="q4tfw6jsg9ys">
          {/* Customer Information */}
          <div data-id="element-5031" data-mp="142q8krv4fw86">
            <div className="flex items-center mb-3" data-id="element-5032" data-mp="1439knry3bqea">
              <BuildingIcon size={16} className="text-blue-400 mr-2" data-id="element-5033" data-mp="z46pcz7wwyji" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-5034" data-mp="z2vmr2q7dwtz">
                Customer Information
              </h4>
            </div>
            <div className="bg-slate-900/30 rounded-lg p-3 mb-3" data-id="element-5035" data-mp="30wuvphfus23i">
              <div className="text-xs text-blue-300 mb-1" data-id="element-5036" data-mp="1ge0u870spln9">Dealer</div>
              <div className="text-white" data-id="element-5037" data-mp="1gespthue7jdj">
                {quoteData.customerInfo?.dealerName || 'Not specified'}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-5038" data-mp="17m8pmcygd6ql">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5039" data-mp="17jmgw9ztsl6s">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5040" data-mp="1bm923s1zr7v0">Bill To</div>
                <div className="text-white" data-id="element-5041" data-mp="xnsy48vijvgk">
                  {quoteData.customerInfo?.billToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-5042" data-mp="12o73tk9n6tbf">
                  {quoteData.customerInfo?.billToCountry || ''}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5043" data-mp="snodq7rhigl2">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5044" data-mp="sq9gcyzlx0jv">Ship To</div>
                <div className="text-white" data-id="element-5045" data-mp="2j1nj36rgdggy">
                  {quoteData.customerInfo?.shipToCompany || 'Not specified'}
                </div>
                <div className="text-sm text-slate-400 mt-1" data-id="element-5046" data-mp="1ybjlgqv07td2">
                  {quoteData.customerInfo?.shipToCountry || ''}
                </div>
              </div>
            </div>
          </div>
          {/* Quote Details */}
          <div data-id="element-5047" data-mp="1ocly3r9be5vn">
            <div className="flex items-center mb-3" data-id="element-5048" data-mp="1oc2iwib1tvlb">
              <FileTextIcon size={16} className="text-blue-400 mr-2" data-id="element-5049" data-mp="1jf2c256dkot5" />
              <h4 className="text-blue-300 font-medium text-sm" data-id="element-5050" data-mp="1jgwwzjhncgx8">
                Quote Details
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3" data-id="element-5051" data-mp="1alp9ebiie8tv">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5052" data-mp="1ak5vgrkbs9v9">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5053" data-mp="15nxjto5c6ysu">Valid Until</div>
                <div className="text-white" data-id="element-5054" data-mp="10okv0xuwnhnm">
                  {quoteData.shipping?.quoteValidUntil || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5055" data-mp="vqsuxgk3wp8c">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5056" data-mp="roo1oixklum8">Delivery Date</div>
                <div className="text-white" data-id="element-5057" data-mp="33bhss2pcaa68">
                  {quoteData.shipping?.desiredDeliveryDate || 'Not specified'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-5058" data-mp="3cbo7frwrxg6z">
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5059" data-mp="3cc7m8zr4nogs">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5060" data-mp="3har7sz1t6y8m">Payment Terms</div>
                <div className="text-white" data-id="element-5061" data-mp="2jn0mxyjumuuh">
                  {quoteData.shipping?.paymentTerms || 'Not specified'}
                </div>
              </div>
              <div className="bg-slate-900/30 rounded-lg p-3" data-id="element-5062" data-mp="2om3jsvpodurd">
                <div className="text-xs text-blue-300 mb-1" data-id="element-5063" data-mp="2tjvmmkruz1ac">Incoterm</div>
                <div className="text-white" data-id="element-5064" data-mp="2ybdf0kftftjp">
                  {quoteData.shipping?.incoterm || 'Not specified'}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Items Summary */}
        <div className="mb-6" data-id="element-5065" data-mp="25uasr6u62jma">
          <div className="flex items-center mb-3" data-id="element-5066" data-mp="25t9eyulrnj0v">
            <PackageIcon size={16} className="text-blue-400 mr-2" data-id="element-5067" data-mp="1xtv0wrid8az9" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-5068" data-mp="1tt1clx10gu5u">Items Summary</h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-5069" data-mp="1tr8cl0kwl86t">
            {quoteType === 'Pivot' && <div className="text-white" data-id="element-5070" data-mp="27mk7b7z26ynq">
                {quoteData.pivots.length} Pivot Machine
                {quoteData.pivots.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Spare Parts' && <div className="text-white" data-id="element-5071" data-mp="1exloe8n43shd">
                {quoteData.parts.length} Spare Part
                {quoteData.parts.length !== 1 ? 's' : ''}
              </div>}
            {quoteType === 'Project' && <div className="text-white" data-id="element-5072" data-mp="1orlsc76nqroi">
                {quoteData.items.length} Project Item
                {quoteData.items.length !== 1 ? 's' : ''}
              </div>}
          </div>
        </div>
        {/* Financial Summary */}
        <div data-id="element-5073" data-mp="2vatqe24w18b6">
          <div className="flex items-center mb-3" data-id="element-5074" data-mp="2v8z70s99lo97">
            <CircleDollarSignIcon size={16} className="text-blue-400 mr-2" data-id="element-5075" data-mp="h29a3zao8hxc" />
            <h4 className="text-blue-300 font-medium text-sm" data-id="element-5076" data-mp="h4doxca3z1ce">
              Financial Summary
            </h4>
          </div>
          <div className="bg-slate-900/30 rounded-lg p-4" data-id="element-5077" data-mp="81kz7dgkqxoo">
            <div className="space-y-2 mb-4" data-id="element-5078" data-mp="835uj5apqknl">
              <div className="flex justify-between" data-id="element-5079" data-mp="d0e8susqq4jk">
                <span className="text-blue-300" data-id="element-5080" data-mp="d0xummlihd2v">Subtotal:</span>
                <span className="text-white" data-id="element-5081" data-mp="10q09xnsoatu7">
                  {quoteData.shipping?.currency || '$'} {subtotal.toFixed(2)}
                </span>
              </div>
              {discount > 0 && <div className="flex justify-between" data-id="element-5082" data-mp="r0yupztycgha">
                  <span className="text-blue-300" data-id="element-5083" data-mp="r06zwr86ims2">Discount ({discount}%):</span>
                  <span className="text-green-400" data-id="element-5084" data-mp="vy8plv041kci">
                    - {quoteData.shipping?.currency || '$'}{' '}
                    {discountAmount.toFixed(2)}
                  </span>
                </div>}
              {shippingTotal > 0 && <div className="flex justify-between" data-id="element-5085" data-mp="1ohnttlrip4mz">
                  <span className="text-blue-300" data-id="element-5086" data-mp="3kk0wa9fjclfp">Shipping:</span>
                  <span className="text-white" data-id="element-5087" data-mp="3fsiw3nlc6ovs">
                    {quoteData.shipping?.currency || '$'}{' '}
                    {shippingTotal.toFixed(2)}
                  </span>
                </div>}
            </div>
            <div className="flex justify-between pt-3 border-t border-blue-800/30" data-id="element-5088" data-mp="35uuse85r671h">
              <span className="text-white font-medium" data-id="element-5089" data-mp="7cz9h7f684pp">Total:</span>
              <span className="text-xl text-white font-bold" data-id="element-5090" data-mp="7a3argvi1c07">
                {quoteData.shipping?.currency || '$'} {total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4 justify-center" data-id="element-5091" data-mp="3piu2dgshta5u">
        <button onClick={() => setShowPreview(true)} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5092" data-mp="3pkeuz0v8yj12">
          <EyeIcon size={18} className="mr-2" data-id="element-5093" data-mp="2e4zydy6y9t6h" />
          Preview Quote
        </button>
        <button onClick={() => console.log('Print quote')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5094" data-mp="tmpfm6pda55b">
          <PrinterIcon size={18} className="mr-2" data-id="element-5095" data-mp="12k84jm64t3mh" />
          Print Quote
        </button>
        <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2.5 rounded-lg bg-blue-600/20 text-blue-300 hover:bg-blue-600/40 hover:text-white transition-colors" data-id="element-5096" data-mp="17dudb396cnd0">
          <DownloadIcon size={18} className="mr-2" data-id="element-5097" data-mp="1h8m7ry7gmu3k" />
          Download PDF
        </button>
      </div>
      {/* Preview Modal */}
      {showPreview && <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-5098" data-mp="1qavkmuq7azk4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-auto" data-id="element-5099" data-mp="3n3nl185vw1lx">
            <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center" data-id="element-5100" data-mp="3s28nzfcisze2">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-5101" data-mp="gy16c8k4haf2">
                Quote Preview
              </h3>
              <button onClick={() => setShowPreview(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5102" data-mp="lw2ngnxzmznw">
                <span className="text-2xl" data-id="element-5103" data-mp="quxegd63zuj3">&times;</span>
              </button>
            </div>
            <div className="p-6" data-id="element-5104" data-mp="vw394gjbxrv9">
              <QuotePreview quoteData={quoteData} quoteType={quoteType} quoteNumber={quoteNumber} data-id="element-5105" data-mp="vwd1garukb44" />
            </div>
            <div className="sticky bottom-0 bg-gray-50 p-4 border-t flex justify-end" data-id="element-5106" data-mp="zustwu6p6box">
              <button onClick={() => setShowPreview(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg mr-3 hover:bg-gray-300 transition-colors" data-id="element-5107" data-mp="14tncbv0r5y8i">
                Close
              </button>
              <button onClick={() => console.log('Download PDF')} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-5108" data-mp="1endxapxo7qjg">
                <DownloadIcon size={16} className="mr-2" data-id="element-5109" data-mp="1nmrbfvwqp3ai" />
                Download PDF
              </button>
            </div>
          </div>
        </div>}
    </div>;
}